import type { MenuItemSpec } from "@/components/shell/menuSpec";
import type { CardGroup } from "@/lib/groups";

/**
 * The グループに追加 submenu (日本-601), built the same way in all three places it appears — 単語's
 * row menu, 単語's bulk bar, and 復習's `Card ⋯` — so the three cannot disagree about what a tick
 * means. Pure, and tested.
 *
 * **Each group is a toggle over the whole target.** Ticked when EVERY targeted note is already in
 * it, and choosing a ticked group removes them all; choosing an unticked one adds the ones that are
 * missing. A partly-held group is unticked, because the act it offers is "add the rest".
 *
 * Groups arrive most-recently-used first from the server, which is the order kept here: the group
 * you just added a card to is overwhelmingly the one the next card goes into.
 */
export interface GroupMenuActions {
  add: (group: CardGroup, noteIds: number[]) => void;
  remove: (group: CardGroup, noteIds: number[]) => void;
  /** 新しいグループ… — asks for a name, creates the group and adds the notes in one step. */
  create: (noteIds: number[]) => void;
}

export function isInGroup(
  group: CardGroup,
  noteIds: readonly number[],
  membership: ReadonlyMap<number, readonly string[]>,
): boolean {
  return (
    noteIds.length > 0 &&
    noteIds.every((id) => (membership.get(id) ?? []).includes(group.id))
  );
}

export function groupMenuItems(
  groups: readonly CardGroup[],
  noteIds: readonly number[],
  membership: ReadonlyMap<number, readonly string[]>,
  actions: GroupMenuActions,
): MenuItemSpec[] {
  const ids = [...noteIds];
  const rows: MenuItemSpec[] = groups.map((group) => {
    const checked = isInGroup(group, ids, membership);
    return {
      kind: "item",
      id: `group-${group.id}`,
      label: group.name,
      checked,
      onSelect: () => (checked ? actions.remove(group, ids) : actions.add(group, ids)),
    };
  });
  return [
    ...rows,
    ...(rows.length ? [{ kind: "separator", id: "group-sep" } as const] : []),
    { kind: "item", id: "group-new", label: "新しいグループ…", onSelect: () => actions.create(ids) },
  ];
}
